import type { DateFormat } from '../fields/date-field/DateField.types'
import type { DatePickerRenderTriggerSlot } from './date-picker-trigger/DatePickerTrigger.types'
import { ValidationStatuses } from '../fields/base-field'
import { countryCodes } from './DatePickerCalendar.types'

interface DatePickerCommonProps {
  /**
   * Additional CSS class name applied to the field wrapper.
   */
  className?: string

  /**
   * Label text displayed above the date picker trigger.
   */
  label?: string

  /**
   * Helper text displayed below the label.
   */
  helpText?: string

  /**
   * Validation status of the field.
   */
  status?: ValidationStatuses

  /**
   * Feedback text displayed below the field.
   */
  feedbackText?: string

  /**
   * Placeholder text displayed when no date is selected.
   */
  placeholder?: string

  /**
   * Whether the date picker is disabled and cannot be interacted with.
   */
  disabled?: boolean

  /**
   * Whether the calendar is open on initial render.
   */
  defaultOpen?: boolean

  /**
   * Minimum selectable date.
   */
  minDate?: Date | null

  /**
   * Maximum selectable date.
   */
  maxDate?: Date | null

  /**
   * Dates that cannot be selected.
   */
  excludeDates?: Date[]

  /**
   * Dates that can be selected.
   */
  includeDates?: Date[]

  /**
   * Locale string (e.g., 'en-GB', 'nl-NL') to determine language and date formatting..
   */
  locale?: keyof typeof countryCodes

  /**
   * Date display format used by the trigger value.
   * Supported formats are shared with DateField.
   * @default 'dd.MM.yyyy'
   */
  dateFormat?: DateFormat

  /**
   * Accessible label for the next month button.
   */
  nextMonthAriaLabel?: string

  /**
   * Accessible label for the previous month button.
   */
  previousMonthAriaLabel?: string

  /**
   * Whether to show week numbers in the calendar.
   */
  showWeekNumbers?: boolean

  /**
   * Label for the week number column when showWeekNumbers is enabled.
   * @default 'Week'
   */
  weekLabel?: string

  /**
   * Number of months displayed side by side in the calendar.
   * @default 1
   */
  monthsShown?: number

}

export interface DatePickerDefaultTriggerProps {
  /**
   * Render slot for the calendar trigger.
   * Defaults to DatePickerTrigger when omitted.
   */
  renderTriggerSlot?: undefined

  /**
   * Accessible label for the default trigger button.
   */
  calendarAriaLabel: string
}

export interface DatePickerCustomTriggerProps {
  /**
   * Render slot for the calendar trigger.
   * Receives picker-controlled props; react-datepicker injects value and interaction handlers into the returned element.
   */
  renderTriggerSlot: DatePickerRenderTriggerSlot

  /**
   * Optional value forwarded to renderTriggerSlot.
   * Accessibility for the trigger is owned by the custom slot implementation.
   */
  calendarAriaLabel?: string
}

export type DatePickerTriggerProps = DatePickerDefaultTriggerProps | DatePickerCustomTriggerProps

export type DatePickerBaseProps =
  | (DatePickerCommonProps & { inline?: false } & DatePickerTriggerProps)
  | (DatePickerCommonProps & {
    /**
     * Whether the calendar is always visible and rendered without a trigger field.
     * When enabled, trigger-related props are ignored.
     * @default false
     */
    inline: true
    /**
     * Not required when inline is true — no trigger is rendered.
     */
    calendarAriaLabel?: string
    renderTriggerSlot?: DatePickerRenderTriggerSlot
  })

export function getDatePickerTriggerProps(
  props: DatePickerBaseProps,
): DatePickerTriggerProps {
  if (props.inline) {
    return { calendarAriaLabel: props.calendarAriaLabel ?? '' }
  }

  if (props.renderTriggerSlot !== undefined) {
    return {
      renderTriggerSlot: props.renderTriggerSlot,
      calendarAriaLabel: props.calendarAriaLabel,
    }
  }

  return {
    calendarAriaLabel: props.calendarAriaLabel,
  }
}

export type SingleDatePickerProps = DatePickerBaseProps & {
  /**
   * Selection mode for a single date.
   */
  mode?: 'single'

  /**
   * Pre-selected date on initial render (uncontrolled mode).
   */
  defaultValue?: Date | null

  /**
   * Selected date in controlled mode.
   */
  value?: Date | null

  /**
   * Called when selected date changes.
   */
  onChange?: (value: Date | null) => void

  /**
   * Start of a visually highlighted date range (does not change selection behaviour).
   */
  highlightStartDate?: Date | null

  /**
   * End of a visually highlighted date range (does not change selection behaviour).
   */
  highlightEndDate?: Date | null
}

export type DateRangePickerProps = DatePickerBaseProps & {
  /**
   * Selection mode for a date range.
   */
  mode: 'range'

  /**
   * Pre-selected range start date on initial render.
   */
  defaultStartDate?: Date | null

  /**
   * Pre-selected range end date on initial render.
   */
  defaultEndDate?: Date | null

  /**
   * Range start date in controlled mode.
   */
  startDate?: Date | null

  /**
   * Range end date in controlled mode.
   */
  endDate?: Date | null

  /**
   * Called when selected range changes.
   */
  onChange?: (value: [Date | null, Date | null] | null) => void
}

export type DatePickerProps = SingleDatePickerProps | DateRangePickerProps
